/**
 * SLA presentation. The state comes from the server; this only chooses how it looks and how
 * the clock reads, so the pill and the queue can never disagree about whether something is
 * breached.
 */
import type { SlaState, TicketListRow } from '@elutide/shared';
import type { PillTone } from '@/features/customers/temp-ui';

export const SLA_TONE: Record<SlaState, PillTone> = {
  ok: 'ok',
  at_risk: 'warn',
  breached: 'crit',
  none: 'neutral',
};

/** "41h" past due, "2h" remaining, "wait 2d" while the customer holds the ball. */
export function slaLabel(row: TicketListRow, now: Date = new Date()): string {
  if (row.status === 'waiting') {
    const since = row.last_agent_msg_at ? Date.parse(row.last_agent_msg_at) : null;
    if (since === null) return 'waiting';
    const days = Math.floor((now.getTime() - since) / 86_400_000);
    return days >= 1 ? `wait ${days}d` : `wait ${Math.max(0, Math.floor((now.getTime() - since) / 3_600_000))}h`;
  }
  const due = row.first_response_at ? row.sla_resolve_due_at : row.sla_first_due_at;
  if (!due) return 'no policy';
  const diffHours = (Date.parse(due) - now.getTime()) / 3_600_000;
  const magnitude = Math.abs(diffHours);
  const unit = magnitude >= 48 ? `${Math.round(magnitude / 24)}d` : `${Math.round(magnitude)}h`;
  return diffHours < 0 ? `${unit} over` : unit;
}
